import { useToast } from '@ais-ui/ui-core';
import React, { useCallback, useState } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useShapeFetch, usePostGroundingConfiguration } from '@ais-ui/api';
import { useGetFilters } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import {
  convertGroundingConfigurationFormValuesToBackend,
  groundingDetectorFormSchema,
  GroundingDetectorFormValues,
} from './groundingForm';
import { mutualDetectorDefaultValues } from '../Mutations/detectorForm';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';
import { GroundingDetectorDialog } from './GroundingDetectorDialog';

interface CreateDetectorDialogProps {
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
}

export const CreateGroundingDetectorDialog = ({
  showDialog,
  setShowDialog,
}: CreateDetectorDialogProps) => {
  const { messages } = useLocalization();
  const { mutate: mutateGrounding } = usePostGroundingConfiguration();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const shapeFetch = useShapeFetch();
  const { toast } = useToast();
  const { data: filters } = useGetFilters();
  const form = useForm<GroundingDetectorFormValues>({
    resolver: zodResolver(groundingDetectorFormSchema),
    defaultValues: {
      ...mutualDetectorDefaultValues,
      useShallowWaters: true,
      useSogDecrease: true,
      filterPorts: true,
      filterAnchorageAreas: true,
      useRemainsGroundedCheck: true,
      validateMinutes: 15,
    },
  });

  const onCreate = useCallback(
    async (data: GroundingDetectorFormValues) => {
      if (!filters) {
        return;
      }
      let shape = null;
      if (data.shapeId) {
        shape = await shapeFetch(data.shapeId);
      }

      if (!shape) {
        return;
      }
      const filter = filters.find((filter) => filter.uuid === data.filterId);
      setIsSubmitting(true);

      const callBacks = mutateCallbacks(
        toast,
        messages,
        'create',
        'detector',
        setIsSubmitting,
        setShowDialog
      );

      const groundingValues = convertGroundingConfigurationFormValuesToBackend(
        data,
        shape,
        filter
      );
      mutateGrounding(
        {
          groundingConfigurationSchema: groundingValues,
        },
        callBacks
      );
    },
    [filters, shapeFetch, mutateGrounding]
  );

  return (
    <GroundingDetectorDialog
      form={form}
      onSubmit={onCreate}
      setShowOuterDialog={setShowDialog}
      mutateType="create"
      isSubmitting={isSubmitting}
    ></GroundingDetectorDialog>
  );
};
